import type { TrustLevel } from './types'

/**
 * The trust levels and the display score each starts at. Mirrors
 * `levelFromScore` in api/internal/auth/trust_score.go — the API sends the
 * level name with every score, so this table is only for the things the API
 * does not say: where the next level starts and how far away it is.
 */
export const TRUST_LEVELS: readonly { level: TrustLevel; min: number }[] = [
  { level: 'Unverified', min: 0 },
  { level: 'Basic', min: 1 },
  { level: 'Verified', min: 20 },
  { level: 'Established', min: 50 },
  { level: 'Veteran', min: 80 },
]

/** The level a display score falls in. */
export function trustLevelFor(score: number): TrustLevel {
  let found: TrustLevel = 'Unverified'
  for (const l of TRUST_LEVELS) {
    if (score >= l.min) found = l.level
  }
  return found
}

/**
 * The next level up and the points still needed to reach it, or null at the
 * top level.
 */
export function nextTrustLevel(score: number): { level: TrustLevel; pointsToGo: number } | null {
  const next = TRUST_LEVELS.find((l) => l.min > score)
  return next ? { level: next.level, pointsToGo: next.min - score } : null
}

/** "6 to Veteran", or "Top level" once there is nowhere left to climb. */
export function nextTrustLevelLabel(score: number): string {
  const next = nextTrustLevel(score)
  return next ? `${next.pointsToGo} to ${next.level}` : 'Top level'
}

/**
 * Whether a game's score lags the overall Carousel score enough to call out:
 * the API's low-game-specific flag, or a game score at least 15 points under
 * the overall one when the flag is missing (cached results predate it).
 */
export function isLaggingGameScore(gameScore: number, overallScore: number, flags?: string[] | null): boolean {
  if (flags?.includes('low_game_specific_trust')) return true
  return overallScore - gameScore >= 15
}
